import {
  InsuranceCaseData,
  InsuranceClaimData,
  ClioSyncAttemptBatchData,
  ClioSyncAttemptData,
  CommunicationEventData,
  EngagementLetterData,
  EventData,
  ExpenseEntryData,
  FinancialInstitutionData,
  FirmData,
  HumanData,
  InvoiceAllocationCoordinatorData,
  InvoiceData,
  InvoiceTransactionAllocationData,
  InvoiceNumberCounterData,
  InvoicePresentationSnapshotData,
  InvoiceTimelineEventData,
  InvoiceVoidOperationData,
  KnowledgeBasePageData,
  MatterDraftInvoiceState,
  MatterData,
  MatterInvoiceBalanceEventData,
  MatterDocumentData,
  NoteData,
  OrganizationData,
  PaySheetData,
  TaskData,
  TemplateData,
  TimeEntryData,
  TransactionData,
  UserData,
  YearMonthDay,
} from '@shared/types';
import {
  isYearMonthDay,
  ymdFromFirestoreString,
  ymdToFirestoreString,
} from './yearMonthDayUtil';
import {
  DocumentReference,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from 'firebase/firestore';

export const generalConverter = <T>() => ({
  toFirestore(data: T): T {
    return cleanValueToFirestore(data);
  },

  fromFirestore(snapshot: QueryDocumentSnapshot<T>): T {
    return cleanDataFromFirestore(snapshot.data());
  },
});

export const insuranceCaseConverter = generalConverter<InsuranceCaseData>();
export const insuranceClaimConverter = generalConverter<InsuranceClaimData>();
export const firmConverter = generalConverter<FirmData>();
export const noteConverter = generalConverter<NoteData>();
export const timeEntryConverter = generalConverter<TimeEntryData>();
export const expenseEntryConverter = generalConverter<ExpenseEntryData>();
export const financialInstitutionConverter =
  generalConverter<FinancialInstitutionData>();
export const taskConverter = generalConverter<TaskData>();
export const humanConverter = generalConverter<HumanData>();
export const organizationConverter = generalConverter<OrganizationData>();
export const knowledgeBasePageConverter =
  generalConverter<KnowledgeBasePageData>();
export const matterConverter = generalConverter<MatterData>();
export const matterInvoiceBalanceEventConverter =
  generalConverter<MatterInvoiceBalanceEventData>();
export const matterDraftInvoiceStateConverter =
  generalConverter<MatterDraftInvoiceState>();
export const userConverter = generalConverter<UserData>();
export const engagementLetterConverter =
  generalConverter<EngagementLetterData>();
export const eventConverter = generalConverter<EventData>();
export const invoiceConverter = generalConverter<InvoiceData>();
export const invoiceTransactionAllocationConverter =
  generalConverter<InvoiceTransactionAllocationData>();
export const invoiceAllocationCoordinatorConverter =
  generalConverter<InvoiceAllocationCoordinatorData>();
export const invoiceNumberCounterConverter =
  generalConverter<InvoiceNumberCounterData>();
export const invoicePresentationSnapshotConverter =
  generalConverter<InvoicePresentationSnapshotData>();
export const invoiceTimelineEventConverter =
  generalConverter<InvoiceTimelineEventData>();
export const invoiceVoidOperationConverter =
  generalConverter<InvoiceVoidOperationData>();
export const matterDocumentConverter = generalConverter<MatterDocumentData>();
export const transactionConverter = generalConverter<TransactionData>();
export const clioSyncAttemptConverter = generalConverter<ClioSyncAttemptData>();
export const clioSyncAttemptBatchConverter =
  generalConverter<ClioSyncAttemptBatchData>();
export const communicationEventConverter =
  generalConverter<CommunicationEventData>();
export const paySheetConverter = generalConverter<PaySheetData>();
export const templateConverter = generalConverter<TemplateData>();

export const cleanDataFromFirestore = <T>(data: T): T => {
  return cleanValueFromFirestore(data);
};

export const cleanDataToFirestore = <T>(data: T): T => {
  return cleanValueToFirestore(data);
};

// -----------------------------------------------------------------------------
// Private functions
// -----------------------------------------------------------------------------

const isFirestoreFieldValueSentinel = (value: unknown): boolean => {
  // Firestore "FieldValue" sentinels (deleteField, serverTimestamp, arrayUnion,
  // increment, etc) are objects with an internal `_methodName` property.
  //
  // Our `cleanValueToFirestore` helper recursively clones plain objects to
  // normalize types (YearMonthDay, Dates, etc). If we recurse into these
  // sentinels, they get converted into JSON-ish maps like `{ _methodName:
  // 'deleteField' }` and end up being persisted as data instead of being applied
  // as an update transform.
  if (typeof value !== 'object' || value === null) return false;
  if (!('_methodName' in value)) return false;
  return typeof (value as any)._methodName === 'string';
};

const cleanValueFromFirestore = (value: any, level: number = 0): any => {
  if (value === null) {
    return undefined;
  }

  if (value instanceof Timestamp) {
    return value.toDate();
  }
  // Firestore doesn't consistently return timestamps on nested objects as
  // proper Timestamp instances.
  //
  // For example:
  // When onDocumentWritten is triggered with a document from the insuranceClaims
  // collection, the top-level `createdTimestamp` value is an instance of Timestamp.
  // The claim's events, however, also have `createdTimestamp` fields. These
  // values appear to be objects that behave like Timestamps, but do not pass
  // the `instanceof Timestamp` check.
  //
  // As of 2025-10-18, I haven't found a pseudo-Timestamp without the
  // `_seconds` and `_nanoseconds` fields, so am using them as a fallback.
  if (
    typeof value === 'object' &&
    value !== null &&
    '_seconds' in value &&
    '_nanoseconds' in value
  ) {
    const seconds = value._seconds as number;
    const nanoseconds = value._nanoseconds as number;
    return new Timestamp(seconds, nanoseconds).toDate();
  }

  if (typeof value === 'string') {
    const ymd = ymdFromFirestoreString(value);
    if (ymd) {
      return ymd;
    }
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    !(value instanceof DocumentReference)
  ) {
    if (Array.isArray(value)) {
      return value.map((v) => cleanValueFromFirestore(v, level + 1));
    }
    const result: any = {};
    for (const key in value) {
      result[key] = cleanValueFromFirestore(value[key], level + 1);
    }
    return result;
  }
  return value;
};

const cleanValueToFirestore = (value: any, level: number = 0): any => {
  // Note: Our firestore settings include `ignoreUndefinedProperties`so we don't
  // need to convert undefined values to null.

  if (isYearMonthDay(value)) {
    return ymdToFirestoreString(value);
  }
  if (value instanceof Date) {
    // Javascript Dates are automatically converted to firestore Timestamps
    return value;
  }
  if (isFirestoreFieldValueSentinel(value)) {
    return value;
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    !(value instanceof DocumentReference)
  ) {
    if (Array.isArray(value)) {
      return value.map((v) => cleanValueToFirestore(v, level + 1));
    }
    const result: any = {};
    for (const key in value) {
      result[key] = cleanValueToFirestore(value[key], level + 1);
    }
    return result;
  }
  return value;
};
